import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Download, Copy, Check, FileSpreadsheet } from "lucide-react";

/**
 * Reusable Export Modal Component
 * Provides direct file download and copy-to-clipboard functionality
 * 
 * NOTE: Always uses client-side download with pre-fetched csvData because
 * direct URL downloads fail authentication in new tabs/windows.
 */
export default function ExportModal({ 
  open, 
  onOpenChange, 
  csvData, 
  filename, 
  downloadUrl  // Kept for backwards compatibility but not used for download
}) {
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef(null);

  // Reset states when modal opens
  useEffect(() => {
    if (open) {
      setCopied(false);
    }
  }, [open]);

  const handleCopyToClipboard = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(csvData);
        setCopied(true);
        toast.success("CSV data copied to clipboard!");
        setTimeout(() => setCopied(false), 3000);
        return;
      }
      
      const textArea = document.createElement('textarea');
      textArea.value = csvData;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (success) {
        setCopied(true);
        toast.success("CSV data copied to clipboard!");
        setTimeout(() => setCopied(false), 3000);
      } else {
        throw new Error("execCommand failed");
      }
    } catch (err) {
      console.error('Copy failed:', err);
      toast.error("Auto-copy failed. Please select the text manually (Ctrl+A) and copy (Ctrl+C)");
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();
      }
    }
  };

  const handleSelectAll = () => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
      toast.info("Text selected - press Ctrl+C (Cmd+C on Mac) to copy");
    }
  };

  // Client-side CSV download - always use this since server URLs fail auth in new tabs
  const handleClientDownload = () => {
    if (!csvData) {
      toast.error("No data to download");
      return;
    }
    
    try {
      // Add BOM for Excel UTF-8 compatibility
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvData], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'export.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Download started!");
    } catch (err) {
      console.error('Download failed:', err);
      toast.error("Download failed. Try copying to clipboard instead.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl bg-slate-900 border-slate-800">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-wider flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Export Data
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Filename Display */}
          <div className="flex items-center justify-between">
            <Label className="text-slate-400">
              Filename: <span className="text-white font-mono">{filename}</span>
            </Label>
            <span className="text-xs text-slate-500">
              {csvData ? `${csvData.split('\n').length} rows` : '0 rows'}
            </span>
          </div>

          {/* CSV Data Preview */}
          <div>
            <Label className="text-slate-400 mb-2 block">CSV Data Preview</Label>
            <textarea
              ref={textareaRef}
              value={csvData || ""}
              readOnly
              className="w-full h-64 p-3 bg-slate-800 border border-slate-700 rounded text-sm font-mono text-slate-300 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="No data available..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {/* Primary: Client-side Download - always works regardless of auth */}
            <Button 
              onClick={handleClientDownload}
              className="bg-blue-600 text-white hover:bg-blue-700 flex-1 sm:flex-none"
              disabled={!csvData}
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
            
            {/* Copy to Clipboard */}
            <Button 
              onClick={handleCopyToClipboard} 
              variant="outline"
              className={`flex-1 sm:flex-none ${copied ? 'bg-emerald-900/30 border-emerald-500' : ''}`}
              disabled={!csvData}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-emerald-400" />
                  <span className="text-emerald-400">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy to Clipboard
                </>
              )}
            </Button>

            {/* Select All (fallback) */}
            <Button 
              onClick={handleSelectAll} 
              variant="ghost"
              className="flex-1 sm:flex-none text-slate-400"
              disabled={!csvData}
            >
              Select All
            </Button>

            {/* Close */}
            <Button 
              onClick={() => onOpenChange(false)} 
              variant="ghost"
              className="flex-1 sm:flex-none"
            >
              Close
            </Button>
          </div>

          {/* Help Text */}
          <div className="bg-slate-800/50 rounded-md p-3 text-center">
            <p className="text-xs text-slate-500">
              Click "Download CSV" to save the file. The data shown above is what will be downloaded.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
